import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { LuImagePlus } from "react-icons/lu";
import { RiDeleteBin6Line } from "react-icons/ri";
import { IoEyeOutline } from "react-icons/io5";
import ImageModal from "../components/ImageModal";
import { safeSetLocalStorage, safeGetParsedLocalStorage } from "../utils/localStorage";

function SellImages({ imagesArr = [], setImagesArr, labels = [], setlabels, setImagesForApi, imagesForApi }) {
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);

    // Load from localStorage
    useEffect(() => {
        const parsedImages = safeGetParsedLocalStorage("sellImages", []);
        if (parsedImages && parsedImages.length > 0) {
            setImagesArr(parsedImages);
        }
    }, []);

    // Convert File to Base64
    const toBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });

    const handleDrop = async (acceptedFiles) => {
        const acceptedTypes = ["image/png", "image/jpeg", "image/jpg"];

        const validFiles = acceptedFiles.filter((file) => acceptedTypes.includes(file.type));

        const imageFiles = validFiles.filter((file) => file.type.startsWith("image/"));
        const nonImageFiles = validFiles.filter((file) => !file.type.startsWith("image/"));

        if (nonImageFiles.length > 0) {
            setErrorMessage("Only image files are allowed");
            return;
        }

        const imageData = await Promise.all(
            imageFiles.map(async (file) => {
                const base64 = await toBase64(file);
                return {
                    preview: URL.createObjectURL(file),
                    path: file.name,
                    base64, // store for persistence
                };
            })
        );

        // Update state and localStorage
        const updatedImages = [...imagesArr, ...imageData];
        setImagesArr(updatedImages);

        // Try to save to localStorage with error handling
        const success = safeSetLocalStorage("sellImages", JSON.stringify(updatedImages), setErrorMessage);
        if (!success) {
            // If storage fails, still keep the images in memory but warn user
            console.warn("Images loaded but not saved to local storage");
        }

        // Keep files for API submission
        setImagesForApi((prev) => [...prev, ...imageFiles]);
        setlabels((prev) => [...prev, ...imageFiles.map(() => "")]);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDrop,
        accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpg", ".jpeg"],
        },

        multiple: true,
    });

    const handleDeleteImage = (index) => {
        const updatedImages = imagesArr.filter((_, i) => i !== index);
        setImagesArr(updatedImages);

        // Try to save to localStorage with error handling
        safeSetLocalStorage("sellImages", JSON.stringify(updatedImages), setErrorMessage);

        setlabels((prev) => prev.filter((_, i) => i !== index));
        setImagesForApi((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="tw-flex tw-justify-center tw-w-full">
            <div className="tw-w-[80%] max-sm:tw-w-[100%]">
                <div className="tw-text-center tw-flex tw-flex-col tw-gap-[10px]">
                    <p className="tw-font-[800] tw-text-[26px] tw-text-black">Upload Images Of Your Property</p>
                    <p className="tw-font-sans tw-font-normal tw-text-sm tw-leading-[150%] tw-text-black">
                        Please upload clear, high-quality images of your property. Make sure the photos are well-lit and capture key areas such as the
                        interior rooms, exterior views, and any unique features. <br />
                        <span className="tw-text-red-500">Please make sure the images are in JPEG, PNG, or JPG format.</span>
                    </p>
                </div>

                {errorMessage && <p className="tw-text-red-500 tw-text-[14px] tw-mt-2">{errorMessage}</p>}

                <div className="tw-flex tw-gap-2 tw-w-full max-sm:tw-flex-wrap">
                    <div className={`${imagesArr.length > 0 ? "tw-w-[40%] max-sm:tw-w-full" : "tw-w-full"}`}>
                        <Box
                            {...getRootProps()}
                            className="uploadButton tw-border tw-w-full tw-rounded-lg tw-h-[250px] tw-mt-3 tw-border-dashed tw-border-primary tw-bg-[#FFF9F7] tw-min-h-[130px] tw-flex tw-justify-center tw-items-center tw-width"
                            style={{ cursor: "pointer" }}
                        >
                            <label className="tw-flex tw-flex-col tw-text-gray-400 tw-items-center tw-gap-3 tw-mb-2">
                                <LuImagePlus className="tw-text-[2.3rem] tw-text-primary" />
                                <span>Drop your files here</span>
                                <button className="tw-bg-primary hover:tw-bg-primary_hover tw-rounded-md tw-p-2 tw-text-white">Choose images</button>
                            </label>
                        </Box>
                        <input {...getInputProps()} accept="image/*" multiple />
                    </div>

                    {imagesArr.length > 0 && (
                        <div className="tw-w-[60%] max-sm:tw-w-full tw-h-[250px] tw-overflow-y-auto tw-mb-3">
                            {imagesArr.map((img, index) => (
                                <div
                                    onClick={() => setSelectedImage(img?.base64 || img?.preview)}
                                    key={index}
                                    className="tw-flex tw-mt-3 tw-items-center tw-justify-between tw-border tw-rounded-md tw-p-1"
                                >
                                    <div className="tw-flex tw-items-center tw-gap-5">
                                        <img src={img?.base64 || img?.preview} className="tw-h-[50px] tw-w-[50px] tw-rounded-md tw-object-contain" />
                                        <p className="tw-text-primary">{img?.path}</p>
                                    </div>
                                    <div className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mr-2">
                                        <IoEyeOutline className="tw-h-[20px] tw-w-[20px] tw-text-primary tw-cursor-pointer" />
                                        <RiDeleteBin6Line
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteImage(index);
                                            }}
                                            className="tw-h-[20px] tw-w-[20px] tw-text-primary tw-cursor-pointer"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {selectedImage && <ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} />}
        </div>
    );
}

export default SellImages;
